import React, { useState, useEffect } from "react";
import "./Subscription.css";
import Sidebar from "../Sidebar/Sidebar";
import { cloudApi } from "../../api";
import api from "../../api";

const ENV = process.env.REACT_APP_ENV || "production";
const IS_DESKTOP = ENV === "desktop";
const NETWORK_TIMEOUT_MS = 10000;

const Subscription = () => {
  const [loading, setLoading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [downgrading, setDowngrading] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const [error, setError] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("");
  const [billingCycle, setBillingCycle] = useState("MONTHLY");
  const [months, setMonths] = useState(1);
  const [activeSub, setActiveSub] = useState(null);
  const [pendingSub, setPendingSub] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // ── React modal states ──
  const [confirmDialog, setConfirmDialog] = useState({
    visible: false,
    title: "",
    message: "",
    onConfirm: null,
  });
  const [alertDialog, setAlertDialog] = useState({
    visible: false,
    title: "",
    message: "",
    onClose: null,
  });

  const plansRef = React.useRef(null);

  // ── React confirm helper ──
  const showConfirm = (title, message) => {
    return new Promise((resolve) => {
      setConfirmDialog({
        visible: true,
        title,
        message,
        onConfirm: (result) => {
          setConfirmDialog({ visible: false, title: "", message: "", onConfirm: null });
          resolve(result);
        },
      });
    });
  };

  // ── React alert helper ──
  const showAlert = (title, message) => {
    return new Promise((resolve) => {
      setAlertDialog({
        visible: true,
        title,
        message,
        onClose: () => {
          setAlertDialog({ visible: false, title: "", message: "", onClose: null });
          resolve();
        },
      });
    });
  };

  // ── Online/offline listener ──
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // ── Polling for subscription activation ──
  const pollForSubscriptionActivation = async (reference, maxAttempts = 10) => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        await new Promise(resolve => setTimeout(resolve, 2000));
        const fetchPromise = cloudApi.get("/subscriptions/my/subscription");
        const res = IS_DESKTOP
          ? await fetchPromise
          : await Promise.race([
              fetchPromise,
              new Promise((_, reject) =>
                setTimeout(() => reject(new Error("NETWORK_TIMEOUT")), NETWORK_TIMEOUT_MS)
              ),
            ]);
        let active = null;
        if (res.data.active && res.data.active.id) {
          active = res.data.active;
        } else if (res.data.trialing && res.data.trialing.id) {
          active = res.data.trialing;
        } else if (res.data.id && res.data.status && (res.data.status === "ACTIVE" || res.data.status === "TRIALING")) {
          active = res.data;
        }
        if (active && active.status === "ACTIVE") {
          setActiveSub(active);
          setPendingSub(null);
          window.history.replaceState({}, document.title, window.location.pathname);
          await showAlert("✅ Payment Successful", "Your subscription is now active.");
          return true;
        }
      } catch {
        // non-fatal — keep polling
      }
    }
    return false;
  };

  useEffect(() => {
    const initializeComponent = async () => {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError("⚠️ No authentication token found. Please log in again.");
        setIsLoading(false);
        return;
      }

      if (!navigator.onLine) {
        setIsLoading(false);
        return;
      }

      try {
        await fetchSubscription();
        const params = new URLSearchParams(window.location.search);
        const reference = params.get("reference");
        if (reference) {
          pollForSubscriptionActivation(reference).catch(() => {});
        }
      } catch (err) {
        if (!navigator.onLine || err.code === "ERR_NETWORK" || err.message === "Network Error") {
          setIsOnline(false);
        } else if (err.response?.status === 401) {
          setError("⚠️ Your session has expired. Please log in again.");
        } else {
          setError("⚠️ Failed to initialize. Please refresh the page.");
        }
      } finally {
        setIsLoading(false);
      }
    };

    initializeComponent();
  }, []);

  const plans = [
    {
      name: "FREE",
      description: "Get started with basic features",
      monthlyPrice: 0,
      yearlyPrice: 0,
      yearlySavings: 0,
      freeMonths: 0,
      isFree: true,
      benefits: [
        "🔍 Full access to MyApoti App",
        "Secure data backup",
        "Upload up to 100 drugs",
        "Basic inventory management",
        "Single branch access",
        "1 staff account",
        "Mobile app listing",
        "Email support",
       
      ],
    },
    {
      name: "BASIC",
      description: "Best for neighborhood pharmacies",
      monthlyPrice: 2500,
      yearlyPrice: 20000,
      yearlySavings: 10000,
      // freeMonths: 2,
      isFree: false,
      benefits: [
        "🔍 Full access to MyApoti App",
        "Secure data backup",
        "Unlimited drug uploads",
        "Inventory management",
        "Sales recording",
        "Single branch access",
        "Up to 3 staff accounts",
        "Mobile app listing",
        "Email support",
      ],
    },
    {
      name: "STANDARD",
      description: "For growing pharmacies",
      monthlyPrice: 4500,
      yearlyPrice: 35000,
      yearlySavings: 19000,
      // freeMonths: 2,
      isFree: false,
      benefits: [
        "🔍 Full access to MyApoti App",
        "Secure data backup",
        "Unlimited drug uploads",
        "Inventory management",
        "Sales recording",
        "Up to 3 branches",
        "Up to 7 staff accounts",
        "Low stock alerts",
        "Expiry alerts",
        "Stock transfer",
        "Reports",
        "Mobile app listing",
        "Priority support",
      ],
    },
    {
      name: "PREMIUM",
      description: "For large pharmacy chains",
      monthlyPrice: 9000,
      yearlyPrice: 70000,
      yearlySavings: 38000,
      // freeMonths: 2,
      isFree: false,
      benefits: [
        "🔍 Full access to MyApoti App",
        "Secure data backup",
        "Unlimited drug uploads",
        "Inventory management",
        "Sales recording",
        "Unlimited branches",
        "Unlimited staff accounts",
        "Low stock alerts",
        "Expiry alerts",
        "Stock transfer",
        "Reports & advanced analytics",
        "Mobile app listing",
        "Priority support with dedicated manager",
        "Featured search ranking",
      ],
    },
  ];

  const getToken = () => localStorage.getItem("access_token");

  const getTierLevel = (tier) => {
    const hierarchy = { "free": 0, "basic": 1, "standard": 2, "premium": 3 };
    return hierarchy[tier?.toLowerCase()] || 0;
  };

  const fetchSubscription = async () => {
    const token = getToken();
    if (!token) {
      setError("⚠️ No authentication token found. Please log in again.");
      return;
    }
    try {
      const fetchPromise = cloudApi.get("/subscriptions/my/subscription");
      const res = IS_DESKTOP
        ? await fetchPromise
        : await Promise.race([
            fetchPromise,
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error("NETWORK_TIMEOUT")), NETWORK_TIMEOUT_MS)
            ),
          ]);

      let active = null;
      let pending = null;

      if (res.data.active && res.data.active.id) {
        active = res.data.active;
      } else if (res.data.trialing && res.data.trialing.id) {
        active = res.data.trialing;
      } else if (res.data.id && res.data.status && (res.data.status === "ACTIVE" || res.data.status === "TRIALING")) {
        active = res.data;
      }

      if (res.data.pending && Array.isArray(res.data.pending)) {
        const freshPending = res.data.pending.filter(
          sub => sub.status === "PENDING" && !sub.initialized_at
        );
        pending = freshPending.length > 0 ? freshPending[0] : null;
      }

      setActiveSub(active);
      setPendingSub(pending || null);
      setError("");
    } catch (err) {
      if (err.message === "NETWORK_TIMEOUT") {
        setError("⚠️ Network is slow or unavailable. Please check your connection and try again.");
      } else if (!navigator.onLine || err.code === "ERR_NETWORK" || err.message === "Network Error") {
        setIsOnline(false);
      } else if (err.response?.status === 401) {
        setError("⚠️ Your session has expired. Please log in again.");
      } else if (err.response?.status === 403) {
        setError("⚠️ You don't have permission to view subscriptions.");
      } else if (err.code === "ECONNABORTED") {
        setError("⚠️ Request timeout. Please check your connection.");
      } else {
        setError("⚠️ Failed to load subscription data. Please try again.");
      }
    }
  };

  const handleSubscribe = async () => {
    if (!selectedPlan) { setError("Please select a plan"); return; }
    if (selectedPlan === "FREE") { setError("FREE plan is already active. Upgrade to a paid plan for more features."); return; }
    if (!isOnline) { setError("No internet connection. Please check your network and try again."); return; }
    const token = getToken();
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const res = await cloudApi.post(
        `/subscriptions/subscribe/${selectedPlan}?billing_cycle=${billingCycle}&months=${months}`, {}
      );
      await initiatePayment(res.data.subscription_id);
    } catch (err) {
      if (!navigator.onLine || err.code === "ERR_NETWORK" || err.message === "Network Error") {
        setError("No internet connection. Please check your network and try again.");
      } else {
        setError(err.response?.data?.detail || "Subscription failed");
      }
      setLoading(false);
    }
  };

  const initiatePayment = async (subscriptionId) => {
    const token = getToken();
    if (!token) return;
    setProcessingPayment(true);
    setError("");
    try {
      const res = await cloudApi.post(
        `/subscriptions/subscriptions/${subscriptionId}/payment/initialize`, {}
      );
      const { authorization_url } = res.data;
      if (!authorization_url) throw new Error("No Paystack URL returned");
      if (IS_DESKTOP) {
        window.open(authorization_url, "_blank");
        await showAlert(
          "💳 Complete Payment in Browser",
          "Paystack has opened in your browser. Complete your payment there, then return to this app — your subscription will update automatically."
        );
        pollForSubscriptionActivation("pending", 30).catch(() => {});
      } else {
        window.location.href = authorization_url;
      }
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data;
      if (status === 409) {
        const detail = data?.detail || {};
        const reference = detail?.reference || "N/A";
        const message = detail?.message || "You already have a pending payment";
        setError(`⚠️ ${message}. Reference: ${reference}. Please use the "Pay Now" button above to resume payment.`);
        setProcessingPayment(false);
        setLoading(false);
        return;
      }
      if (!navigator.onLine || err.code === "ERR_NETWORK" || err.message === "Network Error") {
        setError("No internet connection. Please check your network and try again.");
      } else {
        const errorMessage = data?.detail?.message || data?.detail || "Payment initialization failed. Please try again.";
        setError(`❌ ${errorMessage}`);
      }
      setProcessingPayment(false);
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!activeSub) return;
    if (!isOnline) { setError("No internet connection. Please check your network and try again."); return; }
    const confirmed = await showConfirm(
      "⏹️ Stop Auto-Renewal",
      "Are you sure you want to stop auto-renewal? You will keep access until the end of your billing period."
    );
    if (!confirmed) return;
    const token = getToken();
    if (!token) return;
    setCanceling(true);
    setError("");
    try {
      const res = await cloudApi.post("/subscriptions/cancel-subscription", {});
      if (res.data && res.data.message) {
        setError("");
        await showAlert(
          "✅ Auto-Renewal Stopped",
          `Auto-renewal stopped. You will have access until ${new Date(activeSub.end_date).toLocaleDateString()}.`
        );
        await fetchSubscription();
      } else {
        setError(res.data.detail || "Failed to stop auto-renewal");
      }
    } catch (err) {
      if (!navigator.onLine || err.code === "ERR_NETWORK" || err.message === "Network Error") {
        setError("No internet connection. Please check your network and try again.");
      } else {
        setError(err.response?.data?.detail || "Failed to stop auto-renewal. Please try again.");
      }
    } finally {
      setCanceling(false);
    }
  };

  const handleEnableAutoRenew = async () => {
    if (!activeSub) return;
    if (!isOnline) { setError("No internet connection. Please check your network and try again."); return; }
    const confirmed = await showConfirm(
      "♻️ Enable Auto-Renewal",
      "Enable auto-renewal? Your subscription will automatically renew when it expires."
    );
    if (!confirmed) return;
    const token = getToken();
    if (!token) return;
    setRenewing(true);
    setError("");
    try {
      const res = await cloudApi.post("/subscriptions/enable-auto-renew", {});
      if (res.data && res.data.message) {
        setError("");
        await showAlert(
          "✅ Auto-Renewal Enabled",
          `Auto-renewal enabled! Your subscription will renew on ${new Date(res.data.next_billing_date).toLocaleDateString()}.`
        );
        await fetchSubscription();
      } else {
        setError(res.data.detail || "Failed to enable auto-renewal");
      }
    } catch (err) {
      if (!navigator.onLine || err.code === "ERR_NETWORK" || err.message === "Network Error") {
        setError("No internet connection. Please check your network and try again.");
      } else {
        const errorDetail = err.response?.data?.detail || "Failed to enable auto-renewal. Please try again.";
        setError(errorDetail);
        if (err.response?.status === 400) {
          await showAlert("⚠️ Action Required", `${errorDetail}\n\nYou need to renew your subscription manually first to save payment details.`);
        }
      }
    } finally {
      setRenewing(false);
    }
  };

  const handleUpgradeSubscription = async () => {
    if (!activeSub) return;
    if (!selectedPlan) { setError("Please select a plan to upgrade to"); return; }
    if (selectedPlan === activeSub.tier && billingCycle === activeSub.billing_cycle) { setError("Please select a different plan or billing cycle"); return; }
    if (!isOnline) { setError("No internet connection. Please check your network and try again."); return; }
    const token = getToken();
    if (!token) return;
    setUpgrading(true);
    setError("");
    try {
      const endpoint = `/subscriptions/upgrade-subscription/${selectedPlan}?billing_cycle=${billingCycle}`;
      const res = await cloudApi.post(endpoint, {});
      if (res.data.message) {
        const confirmed = await showConfirm(
          "⬆️ Confirm Upgrade",
          `Upgrade Details:\n\n• Old Plan: ${res.data.old_tier} (${res.data.old_billing_cycle})\n• New Plan: ${res.data.new_tier} (${res.data.new_billing_cycle})\n• Credit from old: ₦${res.data.credit_from_old?.toLocaleString()}\n• New price: ₦${res.data.new_price?.toLocaleString() || res.data.new_base_price?.toLocaleString()}\n• Adjusted amount: ₦${res.data.adjusted_amount?.toLocaleString()}\n\nProceed to payment?`
        );
        if (confirmed) {
          if (res.data.payment_required === false) {
            setError("");
            await showAlert("✅ Upgrade Successful", `Your ${res.data.new_tier} plan is now active.`);
            await fetchSubscription();
          } else {
            await initiatePayment(res.data.new_subscription_id);
          }
        }
      }
    } catch (err) {
      if (!navigator.onLine || err.code === "ERR_NETWORK" || err.message === "Network Error") {
        setError("No internet connection. Please check your network and try again.");
      } else {
        setError(err.response?.data?.detail || "Failed to upgrade subscription. Please try again.");
      }
    } finally {
      setUpgrading(false);
    }
  };

  const handleDowngradeSubscription = async () => {
    if (!activeSub) return;
    if (!selectedPlan) { setError("Please select a plan to downgrade to"); return; }
    if (selectedPlan === activeSub.tier) { setError("Please select a different plan"); return; }
    const currentLevel = getTierLevel(activeSub.tier);
    const newLevel = getTierLevel(selectedPlan);
    if (newLevel > currentLevel) { setError("Please use the 'Upgrade Plan' button to upgrade to a higher tier."); return; }
    if (!isOnline) { setError("No internet connection. Please check your network and try again."); return; }
    const token = getToken();
    if (!token) return;
    setDowngrading(true);
    setError("");
    try {
      const endpoint = `/subscriptions/downgrade-subscription/${selectedPlan}`;
      const res = await cloudApi.post(endpoint, {});
      if (res.data.status === "success") {
        await showAlert(
          "✅ Downgrade Scheduled",
          `Current Plan: ${activeSub.tier}\nCurrent Plan Ends: ${new Date(res.data.current_plan_ends).toLocaleDateString()}\n\nNew Plan: ${res.data.new_plan}\nNew Plan Starts: ${new Date(res.data.effective_date).toLocaleDateString()}\n\nAmount Due: ₦${res.data.amount_due?.toLocaleString()}\n\n${res.data.note}`
        );
        setError("");
        setSelectedPlan("");
        await fetchSubscription();
      }
    } catch (err) {
      if (!navigator.onLine || err.code === "ERR_NETWORK" || err.message === "Network Error") {
        setError("No internet connection. Please check your network and try again.");
      } else {
        setError(err.response?.data?.detail || "Failed to downgrade subscription. Please try again.");
      }
    } finally {
      setDowngrading(false);
    }
  };

  const handleRenewSubscription = async () => {
    if (!activeSub) return;
    if (pendingSub) { setError("❌ You already have a pending subscription payment. Please complete that payment first."); return; }
    if (!isOnline) { setError("No internet connection. Please check your network and try again."); return; }
    const token = getToken();
    if (!token) return;
    setRenewing(true);
    setError("");
    try {
      const res = await cloudApi.post(`/subscriptions/subscriptions/renew`, {});
      if (res.data.status === "success") {
        const confirmed = await showConfirm(
          "🔄 Confirm Renewal",
          `Renewal Details:\n\n• Plan: ${res.data.tier} (${res.data.billing_cycle})\n• Current End Date: ${new Date(res.data.current_end_date).toLocaleDateString()}\n• New End Date: ${new Date(res.data.new_end_date).toLocaleDateString()}\n• Amount Due: ₦${res.data.amount_due?.toLocaleString()}\n• Duration: ${res.data.duration_days} days\n\nProceed to payment?`
        );
        if (confirmed) {
          if (res.data.authorization_url) {
            if (IS_DESKTOP) {
              window.open(res.data.authorization_url, "_blank");
              await showAlert(
                "💳 Complete Payment in Browser",
                "Paystack has opened in your browser. Complete your payment there, then return to this app — your subscription will update automatically."
              );
              pollForSubscriptionActivation("pending", 30).catch(() => {});
            } else {
              window.location.href = res.data.authorization_url;
            }
          } else {
            setError("Failed to get payment link. Please try again.");
          }
        }
      }
    } catch (err) {
      if (!navigator.onLine || err.code === "ERR_NETWORK" || err.message === "Network Error") {
        setError("No internet connection. Please check your network and try again.");
      } else {
        setError(err.response?.data?.detail || "Failed to renew subscription. Please try again.");
      }
    } finally {
      setRenewing(false);
    }
  };

  const handleProceedToPendingPayment = async () => {
    if (!pendingSub) return;
    if (!isOnline) { setError("No internet connection. Please check your network and try again."); return; }
    const token = getToken();
    if (!token) return;
    setProcessingPayment(true);
    setError("");
    try {
      const res = await cloudApi.post(
        `/subscriptions/subscriptions/${pendingSub.id}/payment/initialize`, {}
      );
      const { authorization_url } = res.data;
      if (!authorization_url) throw new Error("No Paystack URL returned");
      if (IS_DESKTOP) {
        window.open(authorization_url, "_blank");
        await showAlert(
          "💳 Complete Payment in Browser",
          "Paystack has opened in your browser. Complete your payment there, then return to this app — your subscription will update automatically."
        );
        pollForSubscriptionActivation("pending", 30).catch(() => {});
      } else {
        window.location.href = authorization_url;
      }
    } catch (err) {
      if (!navigator.onLine || err.code === "ERR_NETWORK" || err.message === "Network Error") {
        setError("No internet connection. Please check your network and try again.");
      } else {
        setError(err.response?.data?.detail || "Payment initialization failed. Please try again.");
      }
      setProcessingPayment(false);
    }
  };

  const handleActivatePaidPlan = () => {
    setSelectedPlan(activeSub.tier);
    setError("");
    setTimeout(() => {
      plansRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const totalPrice = () => {
    const plan = plans.find((p) => p.name === selectedPlan);
    if (!plan) return 0;
    return billingCycle === "YEARLY" ? plan.yearlyPrice : plan.monthlyPrice * months;
  };

  const selectedPlanDetails = plans.find((p) => p.name === selectedPlan);

  const getActionButton = () => {
    if (!activeSub) {
      return (
        <button
          onClick={handleSubscribe}
          disabled={loading || processingPayment || !selectedPlan || !isOnline}
          style={{
            opacity: loading || processingPayment || !selectedPlan || !isOnline ? 0.6 : 1,
            cursor: loading || processingPayment || !selectedPlan || !isOnline ? "not-allowed" : "pointer",
            padding: "0.9rem 2rem", fontSize: "1rem", fontWeight: "500",
            background: "#007bff", color: "white", border: "none", borderRadius: "6px", transition: "all 0.3s ease",
          }}
        >
          {processingPayment ? "🔄 Redirecting to Paystack..." : "Subscribe & Pay"}
        </button>
      );
    }
    if (activeSub.status === "TRIALING") {
      if (!selectedPlan) return <p style={{ color: "#999", fontSize: "0.9rem" }}>Select a plan to subscribe</p>;
      if (selectedPlan === "FREE") {
        return (
          <button disabled={true} style={{ opacity: 0.6, cursor: "not-allowed", padding: "0.9rem 2rem", fontSize: "1rem", fontWeight: "500", background: "#6c757d", color: "white", border: "none", borderRadius: "6px" }}>
            FREE plan (automatic fallback)
          </button>
        );
      }
      return (
        <button
          onClick={handleSubscribe}
          disabled={loading || processingPayment || !isOnline}
          style={{ opacity: loading || processingPayment || !isOnline ? 0.6 : 1, cursor: loading || processingPayment || !isOnline ? "not-allowed" : "pointer", padding: "0.9rem 2rem", fontSize: "1rem", fontWeight: "500", background: "#007bff", color: "white", border: "none", borderRadius: "6px", transition: "all 0.3s ease" }}
        >
          {processingPayment ? "🔄 Redirecting to Paystack..." : "💳 Subscribe"}
        </button>
      );
    }
    if (!selectedPlan) return <p style={{ color: "#999", fontSize: "0.9rem" }}>Select a plan to renew, upgrade, or downgrade your subscription</p>;
    if (selectedPlan === "FREE") return <p style={{ color: "#666", fontSize: "0.9rem", fontStyle: "italic" }}>FREE tier is the automatic fallback. Cancel your subscription to revert to FREE.</p>;
    const currentLevel = getTierLevel(activeSub.tier);
    const selectedLevel = getTierLevel(selectedPlan);
    const isSamePlanAndCycle = selectedPlan === activeSub.tier && billingCycle === activeSub.billing_cycle;
    const isSameTierDifferentCycle = selectedPlan === activeSub.tier && billingCycle !== activeSub.billing_cycle;
    const btnBase = { padding: "0.9rem 2rem", fontSize: "1rem", fontWeight: "500", border: "none", borderRadius: "6px", transition: "all 0.3s ease", color: "white" };
    if (isSamePlanAndCycle) {
      return <button onClick={handleRenewSubscription} disabled={renewing || loading || processingPayment || !isOnline} style={{ ...btnBase, opacity: renewing || loading || processingPayment || !isOnline ? 0.6 : 1, cursor: renewing || loading || processingPayment || !isOnline ? "not-allowed" : "pointer", background: "#17a2b8" }}>{renewing ? "⏳ Processing..." : "🔄 Renew Same Plan"}</button>;
    }
    if (isSameTierDifferentCycle) {
      return <button onClick={handleUpgradeSubscription} disabled={upgrading || loading || processingPayment || !isOnline} style={{ ...btnBase, opacity: upgrading || loading || processingPayment || !isOnline ? 0.6 : 1, cursor: upgrading || loading || processingPayment || !isOnline ? "not-allowed" : "pointer", background: "#28a745" }}>{upgrading ? "⏳ Processing..." : `💰 Change to ${billingCycle}`}</button>;
    }
    if (selectedLevel > currentLevel) {
      return <button onClick={handleUpgradeSubscription} disabled={upgrading || loading || processingPayment || !isOnline} style={{ ...btnBase, opacity: upgrading || loading || processingPayment || !isOnline ? 0.6 : 1, cursor: upgrading || loading || processingPayment || !isOnline ? "not-allowed" : "pointer", background: "#28a745" }}>{upgrading ? "⏳ Processing..." : "⬆️ Upgrade Plan"}</button>;
    }
    if (selectedLevel < currentLevel && selectedPlan !== "FREE") {
      return <button onClick={handleDowngradeSubscription} disabled={downgrading || loading || processingPayment || !isOnline} style={{ ...btnBase, opacity: downgrading || loading || processingPayment || !isOnline ? 0.6 : 1, cursor: downgrading || loading || processingPayment || !isOnline ? "not-allowed" : "pointer", background: "#ffc107", color: "#333" }}>{downgrading ? "⏳ Processing..." : "⬇️ Downgrade Plan"}</button>;
    }
    if (selectedPlan === "FREE") return <p style={{ color: "#666", fontSize: "0.9rem", fontStyle: "italic" }}>FREE tier is the automatic fallback. Cancel your subscription to revert to FREE.</p>;
  };

  // ── No internet screen ──
  if (!isOnline) {
    return (
      <div className="subscription-layout">
        <Sidebar />
        <div className="subscription-container" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", textAlign: "center" }}>
          <div style={{ fontSize: "4rem", marginBottom: "1rem" }}>📡</div>
          <h2 style={{ color: "#333", marginBottom: "0.5rem" }}>No Internet Connection</h2>
          <p style={{ color: "#666", maxWidth: "400px", lineHeight: "1.6", marginBottom: "1.5rem" }}>
            Subscription management requires an internet connection. Please check your network and try again.
          </p>
          <button
            onClick={() => {
              if (navigator.onLine) { setIsOnline(true); setIsLoading(true); window.location.reload(); }
              else setError("Still no internet connection. Please check your network settings.");
            }}
            style={{ padding: "0.75rem 2rem", background: "#007bff", color: "white", border: "none", borderRadius: "6px", fontSize: "1rem", cursor: "pointer", fontWeight: "500" }}
          >
            🔄 Retry
          </button>
          {error && <p style={{ color: "#dc3545", marginTop: "1rem", fontSize: "0.9rem" }}>{error}</p>}
        </div>
      </div>
    );
  }

  // ── Loading screen ──
  if (isLoading) {
    return (
      <div className="subscription-layout">
        <Sidebar />
        <div className="subscription-container" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", gap: "1rem" }}>
          <div style={{ width: "44px", height: "44px", border: "4px solid #f0f0f0", borderTop: "4px solid #007bff", borderRadius: "50%", animation: "spin 0.9s linear infinite" }} />
          <p style={{ color: "#555", fontSize: "1rem", margin: 0, fontWeight: "500" }}>Loading subscription...</p>
          <p style={{ color: "#999", fontSize: "0.85rem", margin: 0 }}>Fetching your plan details</p>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-layout">
      <Sidebar />
      <div className="subscription-container">

        {/* ── React Alert Modal ── */}
        {alertDialog.visible && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
            <div style={{ background: "#fff", borderRadius: "10px", padding: "2rem", maxWidth: "460px", width: "90%", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
              <h3 style={{ margin: "0 0 1rem", color: "#333", fontSize: "1.1rem" }}>{alertDialog.title}</h3>
              <p style={{ color: "#555", fontSize: "0.95rem", lineHeight: 1.6, margin: "0 0 1.5rem", whiteSpace: "pre-line" }}>{alertDialog.message}</p>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button
                  onClick={alertDialog.onClose}
                  style={{ padding: "0.6rem 1.5rem", background: "#007bff", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: 600, fontSize: "0.95rem" }}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── React Confirm Modal ── */}
        {confirmDialog.visible && (
          <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
            <div style={{ background: "#fff", borderRadius: "10px", padding: "2rem", maxWidth: "460px", width: "90%", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
              <h3 style={{ margin: "0 0 1rem", color: "#333", fontSize: "1.1rem" }}>{confirmDialog.title}</h3>
              <p style={{ color: "#555", fontSize: "0.95rem", lineHeight: 1.6, margin: "0 0 1.5rem", whiteSpace: "pre-line" }}>{confirmDialog.message}</p>
              <div style={{ display: "flex", gap: "1rem", justifyContent: "flex-end" }}>
                <button
                  onClick={() => confirmDialog.onConfirm(false)}
                  style={{ padding: "0.6rem 1.25rem", background: "#f8f9fa", color: "#333", border: "1px solid #ddd", borderRadius: "6px", cursor: "pointer", fontWeight: 500, fontSize: "0.95rem" }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => confirmDialog.onConfirm(true)}
                  style={{ padding: "0.6rem 1.25rem", background: "#007bff", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: 600, fontSize: "0.95rem" }}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}

        <h2>📦 Manage Your Subscription</h2>

        <div style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white", padding: "2rem", borderRadius: "12px", marginBottom: "2rem", textAlign: "center" }}>
          <h3 style={{ marginTop: 0, marginBottom: "1rem", fontSize: "1.5rem" }}>💎 MyApoti Subscription Plans</h3>
          <p style={{ marginBottom: "0.75rem", fontSize: "1rem", lineHeight: "1.6" }}>
            All subscription tiers include <strong>free access and visibility on the MyApoti app</strong>, giving your pharmacy exposure to thousands of customers.
          </p>
          <p style={{ marginBottom: 0, fontSize: "0.95rem", opacity: 0.95 }}>
            Choose a plan that fits your pharmacy's needs and unlock powerful tools to grow your business.
          </p>
        </div>

        {!isOnline && (
          <div style={{ background: "#fff3cd", border: "1px solid #ffc107", borderRadius: "8px", padding: "1rem", marginBottom: "1rem", display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <span style={{ fontSize: "1.5rem" }}>📡</span>
            <div>
              <strong style={{ color: "#856404" }}>No internet connection</strong>
              <p style={{ margin: 0, color: "#856404", fontSize: "0.9rem" }}>Subscription actions require internet. Please reconnect to continue.</p>
            </div>
          </div>
        )}

        {error && (
          <div style={{ background: "#fff5f5", border: "1px solid #dc3545", borderRadius: "8px", padding: "0.75rem 1rem", marginBottom: "1rem", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.75rem", fontSize: "0.9rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
              <span style={{ fontSize: "1.3rem" }}>⚠️</span>
              <p style={{ margin: 0, color: "#dc3545" }}>{error}</p>
            </div>
            <button
              onClick={() => window.location.reload()}
              style={{ padding: "0.4rem 1rem", background: "#dc3545", color: "white", border: "none", borderRadius: "5px", cursor: "pointer", fontSize: "0.85rem", whiteSpace: "nowrap" }}
            >
              🔄 Retry
            </button>
          </div>
        )}

        {pendingSub && (
          <div style={{ background: "#fff3cd", border: "2px solid #ffc107", borderRadius: "12px", padding: "1.5rem", marginBottom: "2rem", textAlign: "left" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1.5rem" }}>
              <div>
                <h3 style={{ marginTop: 0, color: "#856404" }}>⏳ Pending Subscription</h3>
                <p style={{ marginBottom: "0.5rem" }}>
                  <strong>Plan:</strong> {pendingSub.tier} ({pendingSub.billing_cycle}) <br />
                  <strong>Start Date:</strong> {new Date(pendingSub.start_date).toLocaleDateString()} <br />
                  <strong>End Date:</strong> {new Date(pendingSub.end_date).toLocaleDateString()} <br />
                  <strong>Amount:</strong> ₦{pendingSub.total_amount?.toLocaleString()} <br />
                  <strong>Status:</strong> {pendingSub.status} <br />
                  <strong>Reference:</strong>{" "}
                  <code style={{ background: "#f0f0f0", padding: "0.25rem 0.5rem", borderRadius: "4px" }}>{pendingSub.reference || "N/A"}</code>
                </p>
              </div>
              <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", justifyContent: "flex-end" }}>
                <button
                  onClick={handleProceedToPendingPayment}
                  disabled={processingPayment || !isOnline}
                  style={{ opacity: processingPayment || !isOnline ? 0.6 : 1, cursor: processingPayment || !isOnline ? "not-allowed" : "pointer", padding: "0.75rem 1.5rem", fontSize: "0.9rem", fontWeight: "500", background: "#ffc107", color: "#333", border: "none", borderRadius: "6px", transition: "all 0.3s ease", whiteSpace: "nowrap" }}
                >
                  {processingPayment ? "⏳ Redirecting to Payment..." : "💳 Resume Payment"}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeSub ? (
          <div className="current-sub" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1.5rem", background: activeSub.status === "TRIALING" ? "linear-gradient(135deg, #fff3cd 0%, #ffe69c 100%)" : "white", border: activeSub.status === "TRIALING" ? "2px solid #ffc107" : "1px solid #ddd", padding: "1.5rem", borderRadius: "12px" }}>
            <div>
              <h3 style={{ marginTop: 0, marginBottom: "0.5rem" }}>
                {activeSub.status === "TRIALING" ? "🎉 FREE TRIAL ACTIVE 🎉" : `✨ Current Plan: ${activeSub.tier}`}
              </h3>
              {activeSub.status === "TRIALING" && (
                <p style={{ background: "#fff", padding: "0.75rem", borderRadius: "6px", marginBottom: "1rem", border: "2px solid #ffc107", fontWeight: "bold", color: "#856404", fontSize: "0.95rem" }}>
                  ⏰ You're currently enjoying <strong>{activeSub.tier}</strong> access for FREE
                </p>
              )}
              <p>
                <strong>Plan:</strong> {activeSub.tier} <br />
                <strong>Billing Cycle:</strong> {activeSub.billing_cycle} <br />
                <strong>{activeSub.status === "TRIALING" ? "Trial Start:" : "Start Date:"}</strong> {new Date(activeSub.start_date).toLocaleDateString()} <br />
                <strong>{activeSub.status === "TRIALING" ? "Trial Ends:" : "End Date:"}</strong> {new Date(activeSub.end_date).toLocaleDateString()} <br />
                {activeSub.status !== "TRIALING" && <><strong>Status:</strong> {activeSub.status} <br /></>}
                <strong>Cost:</strong>{" "}
                {activeSub.status === "TRIALING"
                  ? "✨ FREE"
                  : activeSub.tier === "FREE"
                  ? "FREE"
                  : (() => {
                      const plan = plans.find(p => p.name === activeSub.tier);
                      if (!plan) return `₦${activeSub.total_amount?.toLocaleString() ?? "N/A"}`;
                      const price = activeSub.billing_cycle === "YEARLY" ? plan.yearlyPrice : plan.monthlyPrice;
                      return `₦${price.toLocaleString()}`;
                    })()
                } <br />
                {activeSub.status === "TRIALING" && (
                  <span style={{ color: "#d32f2f", fontWeight: "bold", marginTop: "0.5rem", display: "block" }}>
                    ⚠️ After trial ends, your account will revert to the FREE tier unless you activate a paid plan.
                  </span>
                )}
              </p>
            </div>
            {activeSub.status === "TRIALING" && (
              <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", justifyContent: "flex-end" }}>
                <button onClick={handleActivatePaidPlan} style={{ padding: "0.75rem 1.5rem", fontSize: "0.9rem", fontWeight: "600", background: "#28a745", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", whiteSpace: "nowrap", marginTop: "0.5rem" }}>
                  💳 Activate Paid Plan
                </button>
              </div>
            )}
            {activeSub.status !== "TRIALING" && (
              <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", justifyContent: "flex-end" }}>
                {activeSub.tier !== "FREE" && (
                  <button onClick={handleRenewSubscription} disabled={renewing || loading || processingPayment || !isOnline} style={{ opacity: renewing || loading || processingPayment || !isOnline ? 0.6 : 1, cursor: renewing || loading || processingPayment || !isOnline ? "not-allowed" : "pointer", padding: "0.75rem 1.5rem", fontSize: "0.9rem", fontWeight: "500", background: "#28a745", color: "white", border: "none", borderRadius: "6px", transition: "all 0.3s ease", whiteSpace: "nowrap", marginTop: "0.5rem" }}>
                    {renewing ? "⏳ Renewing..." : "🔄 Renew"}
                  </button>
                )}
                {activeSub.auto_renew && (
                  <button onClick={handleCancelSubscription} disabled={canceling || loading || processingPayment || !isOnline} style={{ opacity: canceling || loading || processingPayment || !isOnline ? 0.6 : 1, cursor: canceling || loading || processingPayment || !isOnline ? "not-allowed" : "pointer", padding: "0.75rem 1.5rem", fontSize: "0.9rem", fontWeight: "500", background: "#dc3545", color: "white", border: "none", borderRadius: "6px", transition: "all 0.3s ease", whiteSpace: "nowrap", marginTop: "0.5rem" }}>
                    {canceling ? "⏳ Stopping..." : "⏹️ Stop Auto-Renewal"}
                  </button>
                )}
                {!activeSub.auto_renew && activeSub.tier !== "FREE" && (
                  <button onClick={handleEnableAutoRenew} disabled={renewing || loading || processingPayment || !isOnline} style={{ opacity: renewing || loading || processingPayment || !isOnline ? 0.6 : 1, cursor: renewing || loading || processingPayment || !isOnline ? "not-allowed" : "pointer", padding: "0.75rem 1.5rem", fontSize: "0.9rem", fontWeight: "500", background: "#17a2b8", color: "white", border: "none", borderRadius: "6px", transition: "all 0.3s ease", whiteSpace: "nowrap", marginTop: "0.5rem" }}>
                    {renewing ? "⏳ Enabling..." : "♻️ Enable Auto-Renewal"}
                  </button>
                )}
              </div>
            )}
          </div>
        ) : (
          <p style={{ color: "#666", margin: "1rem 0" }}>No active subscription. Choose a plan below to get started!</p>
        )}

        <div style={{ margin: "2rem 0", padding: "1.5rem", background: "#f9f9f9", borderRadius: "8px" }}>
          <h3 style={{ marginBottom: "1rem", marginTop: 0 }}>🗓️ Select Billing Cycle & Duration</h3>
          <div style={{ marginBottom: "1.5rem" }}>
            <p style={{ marginBottom: "0.75rem", fontWeight: "500", color: "#333" }}>Billing Cycle:</p>
            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
              <button onClick={() => setBillingCycle("MONTHLY")} style={{ padding: "0.75rem 1.5rem", border: billingCycle === "MONTHLY" ? "2px solid #007bff" : "1px solid #ddd", background: billingCycle === "MONTHLY" ? "#e7f3ff" : "#fff", borderRadius: "6px", cursor: "pointer", fontWeight: billingCycle === "MONTHLY" ? "600" : "500", transition: "all 0.3s ease", color: "#333" }}>📅 Monthly</button>
              <button onClick={() => setBillingCycle("YEARLY")} style={{ padding: "0.75rem 1.5rem", border: billingCycle === "YEARLY" ? "2px solid #28a745" : "1px solid #ddd", background: billingCycle === "YEARLY" ? "#e8f5e9" : "#fff", borderRadius: "6px", cursor: "pointer", fontWeight: billingCycle === "YEARLY" ? "600" : "500", transition: "all 0.3s ease", color: "#333", position: "relative" }}>
                🎉 Yearly
                <span style={{ position: "absolute", top: "-10px", right: "5px", background: "#28a745", color: "white", fontSize: "0.7rem", padding: "2px 6px", borderRadius: "12px", fontWeight: "bold", whiteSpace: "nowrap" }}>SAVE 2 MONTHS FREE</span>
              </button>
            </div>
          </div>
          {billingCycle === "MONTHLY" && (
            <div>
              <p style={{ marginBottom: "0.75rem", fontWeight: "500", color: "#333" }}>Duration (months):</p>
              <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                <input
                  type="number"
                  min="1"
                  max="24"
                  value={months}
                  onChange={(e) => setMonths(Math.max(1, Math.min(24, Number(e.target.value))))}
                  style={{ width: "80px", padding: "0.65rem 0.75rem", borderRadius: "6px", border: "1px solid #ddd", fontSize: "1rem", textAlign: "center" }}
                />
                <span style={{ color: "#555", fontSize: "0.95rem" }}>month{months !== 1 ? "s" : ""}</span>
              </div>
              <p style={{ margin: "0.5rem 0 0", color: "#999", fontSize: "0.82rem" }}>Enter any number of months (1–24)</p>
            </div>
          )}
          {billingCycle === "YEARLY" && selectedPlan && selectedPlan !== "FREE" && (
            <div style={{ marginTop: "1rem", padding: "0.75rem", background: "#d4edda", border: "1px solid #28a745", borderRadius: "6px", color: "#155724", fontSize: "0.9rem", fontWeight: "500", textAlign: "center" }}>
              ✨ Get 2 MONTHS FREE when you choose yearly billing!
            </div>
          )}
        </div>

        <div style={{ margin: "2rem 0" }} ref={plansRef}>
          <h3 style={{ marginBottom: "1rem" }}>💳 Choose Your Plan</h3>
          <div className="plans" style={{ display: "flex", flexDirection: "row", gap: "1rem", width: "100%", overflowX: "auto", paddingBottom: "0.5rem", paddingLeft: "0.25rem", paddingRight: "0.25rem", boxSizing: "border-box" }}>
            {plans.map((plan) => (
              <div key={plan.name} className={`plan-card ${selectedPlan === plan.name ? "selected" : ""}`} onClick={() => { setSelectedPlan(plan.name); setError(""); }}
                style={{ cursor: "pointer", padding: "1.5rem", border: selectedPlan === plan.name ? "2px solid #007bff" : "1px solid #ddd", borderRadius: "8px", background: selectedPlan === plan.name ? "#f0f8ff" : "#fff", transition: "all 0.3s ease", textAlign: "left", position: "relative", display: "flex", flexDirection: "column", minWidth: "220px", flex: "1 1 220px" }}>
                {plan.isFree && <span style={{ position: "absolute", top: "0.75rem", right: "0.75rem", background: "#28a745", color: "white", padding: "0.25rem 0.75rem", borderRadius: "20px", fontSize: "0.75rem", fontWeight: "bold" }}>✨ FREE</span>}
                <h4 style={{ fontSize: "1.1rem", margin: "0.5rem 0", color: "#333" }}>{plan.name}</h4>
                <p style={{ fontSize: "0.9rem", color: "#666", margin: "0.3rem 0" }}>{plan.description}</p>
                <p style={{ fontSize: "1.2rem", fontWeight: "bold", color: "#007bff", margin: "0.75rem 0" }}>
                  {plan.isFree ? "FREE" : `₦${billingCycle === "YEARLY" ? plan.yearlyPrice.toLocaleString() : plan.monthlyPrice.toLocaleString()}`}
                </p>
                <p style={{ fontSize: "0.85rem", color: "#666" }}>{plan.isFree ? "Forever" : billingCycle === "YEARLY" ? "per year" : "per month"}</p>
                {!plan.isFree && billingCycle === "YEARLY" && <p style={{ fontSize: "0.85rem", color: "#28a745", fontWeight: "500", margin: "0.5rem 0" }}>💚 Save ₦{plan.yearlySavings.toLocaleString()}</p>}
              </div>
            ))}
          </div>
        </div>

        {selectedPlanDetails && (
          <div className="plan-details" style={{ background: "#f0f8ff", border: "1px solid #007bff", borderRadius: "8px", padding: "1.5rem", margin: "1.5rem 0", textAlign: "left" }}>
            <h3 style={{ textAlign: "center", marginBottom: "1rem" }}>✅ {selectedPlanDetails.name} Plan Benefits</h3>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
              {selectedPlanDetails.benefits.map((b, i) => (
                <li key={i} style={{ padding: "0.5rem 0", paddingLeft: "1.5rem", position: "relative", color: "#333", fontSize: "0.95rem" }}>
                  <span style={{ position: "absolute", left: 0, color: "#007bff", fontWeight: "bold" }}>✓</span>
                  {b}
                </li>
              ))}
            </ul>
          </div>
        )}

        {selectedPlan && selectedPlan !== "FREE" && (
          <div style={{ background: "#f9f9f9", border: "1px solid #ddd", borderRadius: "8px", padding: "1.5rem", margin: "1.5rem 0", textAlign: "left" }}>
            <h3 style={{ marginBottom: "1rem", marginTop: 0 }}>💰 Price Breakdown</h3>
            <div style={{ padding: "1rem", background: "#fff", borderRadius: "6px", borderLeft: "4px solid #007bff" }}>
              <p style={{ margin: "0.5rem 0", display: "flex", justifyContent: "space-between" }}>
                <span>Base Price:</span>
                <strong>₦{billingCycle === "YEARLY" ? selectedPlanDetails.yearlyPrice.toLocaleString() : selectedPlanDetails.monthlyPrice.toLocaleString()}{billingCycle === "MONTHLY" && "/month"}</strong>
              </p>
              {billingCycle === "MONTHLY" ? (
                <p style={{ margin: "0.5rem 0", display: "flex", justifyContent: "space-between" }}><span>Duration:</span><strong>{months} month{months !== 1 ? "s" : ""}</strong></p>
              ) : (
                <p style={{ margin: "0.5rem 0", display: "flex", justifyContent: "space-between" }}><span>Billing Cycle:</span><strong>12 months</strong></p>
              )}
              {billingCycle === "YEARLY" && <p style={{ margin: "0.5rem 0", display: "flex", justifyContent: "space-between", color: "#28a745", fontWeight: "bold" }}><span>Free Bonus:</span><strong>+ 2 MONTHS FREE</strong></p>}
              <hr style={{ margin: "0.75rem 0", border: "none", borderTop: "1px solid #eee" }} />
              <p style={{ margin: "0.5rem 0", display: "flex", justifyContent: "space-between", fontSize: "1.2rem", fontWeight: "bold", color: "#007bff" }}><span>Total:</span><strong>₦{totalPrice().toLocaleString()}</strong></p>
            </div>
          </div>
        )}

        <div className="actions" style={{ display: "flex", gap: "1rem", justifyContent: "center", marginTop: "2rem", flexWrap: "wrap" }}>
          {getActionButton()}
        </div>

        <div style={{ marginTop: "2rem", padding: "1rem", background: "#e7f3ff", border: "1px solid #b3d9ff", borderRadius: "6px", textAlign: "center", color: "#0056b3", fontSize: "0.9rem" }}>
          <p style={{ margin: 0 }}>
            💳 You will be redirected to Paystack to complete your payment securely. <br />
            Your subscription will activate immediately after successful payment.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Subscription;